import { FC, KeyboardEvent, useState } from 'react';

import { useI18nContext } from '../../hooks/contexts/i18n.context';
import { ResaleListingGroup } from '../../hooks/contexts/resale-listing.context';
import { ResaleFeesInfoModal } from '../ResaleFeesInfoModal';
import { ResaleListingItem } from './ResaleListingItem';
import * as Style from './styles';

const HIGH_DEMAND_THRESHOLD = 40;

type ResaleListingAccordeonProps = {
  groups: ResaleListingGroup[];
  onSelectGroup: (group: ResaleListingGroup) => void;
};

type AccordionSection = { groupName: string; rows: ResaleListingGroup[] };

function buildSections(groups: ResaleListingGroup[]): AccordionSection[] {
  const map = new Map<string, AccordionSection>();
  for (const g of groups) {
    if (!map.has(g.groupName)) map.set(g.groupName, { groupName : g.groupName, rows : [] });
    map.get(g.groupName)!.rows.push(g);
  }
  return [...map.values()];
}

const ResaleListingAccordeon: FC<ResaleListingAccordeonProps> = ({ groups, onSelectGroup }) => {
  const { translate } = useI18nContext();

  const sections = buildSections(groups);
  const totalStock = groups.reduce((sum, g) => sum + g.count, 0);
  const isHighDemand = totalStock > HIGH_DEMAND_THRESHOLD;

  const [expanded, setExpanded] = useState<Set<string>>(() => {
    const s = new Set<string>();
    if (sections.length > 0) s.add(sections[0].groupName);
    return s;
  });

  const handleToggle = (name: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      next.has(name) ? next.delete(name) : next.add(name);
      return next;
    });
  };

  const handleHeaderKeyDown = (e: KeyboardEvent<HTMLDivElement>, name: string) => {
    if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleToggle(name); }
  };

  // The icon that opened the fees modal: open while set, and focus returns to
  // it on close.
  const [feesInfoTrigger, setFeesInfoTrigger] = useState<HTMLButtonElement | null>(null);

  return (
    <Style.Container id="resale-listing-accordeon">
      {sections.map((section) => {
        const sectionId = section.groupName.toLowerCase().replace(/[^a-z0-9]/g, '-');
        const isOpen = expanded.has(section.groupName);
        const hasFastPass = section.rows.some((r) => r.offerId !== null);
        const stockBadgeLabel = hasFastPass && totalStock > 0
          ? translate(['RESALE_LISTING', isHighDemand ? 'HIGH_DEMAND_BADGE' : 'LAST_UNITS_BADGE'])
          : null;

        return (
          <Style.Group key={section.groupName} id={`resale-listing-group-${sectionId}`}>
            <Style.GroupHeader
              $opened={isOpen}
              role="button"
              tabIndex={0}
              aria-expanded={isOpen}
              aria-controls={`resale-listing-group-body-${sectionId}`}
              id={`resale-listing-group-header-${sectionId}`}
              onClick={() => handleToggle(section.groupName)}
              onKeyDown={(e) => handleHeaderKeyDown(e, section.groupName)}
            >
              <Style.GroupHeaderLeft>
                <Style.GroupTitleRow>
                  <Style.GroupName id={`resale-listing-group-name-${sectionId}`}>
                    {section.groupName}
                  </Style.GroupName>
                  {stockBadgeLabel && (
                    <Style.StockBadge id={`resale-listing-group-stock-badge-${sectionId}`}>
                      {isHighDemand && <span aria-hidden="true">↗</span>}
                      {stockBadgeLabel}
                    </Style.StockBadge>
                  )}
                </Style.GroupTitleRow>
              </Style.GroupHeaderLeft>
              <Style.CircularArrow $opened={isOpen} aria-hidden="true" id={`resale-listing-group-arrow-${sectionId}`}>
                ▼
              </Style.CircularArrow>
            </Style.GroupHeader>

            <Style.GroupBody
              $opened={isOpen}
              role="region"
              id={`resale-listing-group-body-${sectionId}`}
              aria-labelledby={`resale-listing-group-header-${sectionId}`}
            >
              <Style.GroupBodyInner>
                <Style.ItemList>
                  {section.rows.map((row) => (
                    <ResaleListingItem
                      key={row.uuid}
                      row={row}
                      focusable={isOpen}
                      onSelect={onSelectGroup}
                      onOpenFeesInfo={setFeesInfoTrigger}
                    />
                  ))}
                </Style.ItemList>
              </Style.GroupBodyInner>
            </Style.GroupBody>
          </Style.Group>
        );
      })}
      {/* Outside the rows: portal events bubble up the React tree and must not
          reach a row's or header's onClick/onKeyDown. */}
      <ResaleFeesInfoModal
        open={feesInfoTrigger !== null}
        onClose={() => setFeesInfoTrigger(null)}
        returnFocusTo={feesInfoTrigger}
      />
    </Style.Container>
  );
};

export { ResaleListingAccordeon };
